import { ErrorHandler, Injectable, Injector, NgZone } from '@angular/core';
import * as Sentry from '@sentry/angular';
import { Store } from '@ngrx/store';
import { map, take } from 'rxjs/operators';
import { baseAuthSelectors, baseAuthActions } from '../../_store';
import { BaseTokenInterface } from '../../shared/interfaces/base-token-service.interface';
import { BaseGlobalErrorService } from '../services/base-global-error.service';
import { BaseRolesService } from '../services/base-roles.service';
import { EnvironmentUtils } from '../utilities/environment-utils';
import { BASE_CUSTOM_ERROR_ALERTS } from '../constants/base-custom-error-alerts';

@Injectable()
export class BaseGlobalErrorHandler implements ErrorHandler {
  constructor(private injector: Injector, private globalErrorService: BaseGlobalErrorService, private zone: NgZone) {}

  handleError(error: any): void {
    if (this.handleChunkError(error)) {
      return;
    }

    this.handleSentryError(error);

    const handlerFunc = this.globalErrorService.registerError(error);
    this.zone.run(() => handlerFunc());
  }

  private handleChunkError(error: any): boolean {
    const chunkFailedMessage = /Loading chunk \S+ failed/;

    if (error?.message && chunkFailedMessage.test(error.message)) {
      if (!navigator.onLine) {
        return true;
      }

      const attemptsKey = 'chunkReloadAttempted';
      const hasReloaded = sessionStorage.getItem(attemptsKey);

      if (!hasReloaded) {
        sessionStorage.setItem(attemptsKey, '1');
        setTimeout(() => {
          window.location.reload();
        }, 200);
      } else {
        sessionStorage.removeItem(attemptsKey);

        const store = this.injector.get(Store);
        store.dispatch(baseAuthActions.logout());
      }
      return true;
    }

    return false;
  }

  private handleSentryError(error: any): void {
    const alertErrorIndex = BASE_CUSTOM_ERROR_ALERTS.findIndex(
      (alert) =>
        alert?.ignoreBySentry && alert?.errorMessage?.toLowerCase().includes(error?.message?.toLowerCase() + '')
    );

    if (EnvironmentUtils.getEnvironment() === 'dev' || alertErrorIndex > -1) {
      return;
    }

    if (error?.status === 0) {
      return;
    }

    const store = this.injector.get(Store);
    const roleService = this.injector.get(BaseRolesService);

    store
      .select(baseAuthSelectors.getUserInfo)
      .pipe(
        map((state) => (state.ok ? (state.data as BaseTokenInterface) : null)),
        take(1)
      )
      .subscribe((user) => {
        Sentry.withScope((scope) => {
          if (user && (roleService.isPsp() || roleService.isCustomer())) {
            scope.setUser({
              userId: user.id,
              roles: user.roles.toString(),
              organizationId: user.organizationId,
            });
          }

          scope.setTag('http_status', String(error?.status ?? 'na'));
          const url = error?.url;
          if (url) {
            scope.setTag('api_url', url);
          }

          Sentry.captureException(error);
        });
      });
  }
}
